import React, { useState, type JSX } from 'react';
import { Box, LoadingOverlay, Alert, Text } from '@mantine/core';
import { IconCheck, IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { Check } from 'lucide-react';
import { showNotification } from '@mantine/notifications';
import { useRevenueCat } from '../contexts/RevenueCatContext';
import { type Package } from '@revenuecat/purchases-js';

const FEATURES = [
  'AI Voice conversations',
  'Unlimited text conversations',
  'Mood tracking & pattern analysis',
  'Personalized growth insights',
  'Extended conversation history',
  'Advanced emotional awareness tools',
  'Tailored wellness recommendations',
];

/**
 * RevenueCat-based Subscription Manager
 *
 * Single-tier pricing: Premium (Voice Connect) only
 * Shows Monthly vs Annual as two side-by-side cards
 * Enable by setting VITE_REVENUECAT_API_KEY in environment.
 */
export function RevenueCatSubscriptionManager(): JSX.Element {
  const {
    isLoading,
    isInitialized,
    isPremium,
    isTrialActive,
    trialDaysRemaining,
    availablePackages,
    purchase,
    error: contextError,
  } = useRevenueCat();

  const [purchasing, setPurchasing] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');

  const handlePurchase = async (pkg: Package): Promise<void> => {
    setPurchasing(true);

    const result = await purchase(pkg);

    if (result.success) {
      showNotification({
        title: 'Subscription Activated',
        message: 'Welcome to Voice Connect! Your subscription is now active.',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } else {
      showNotification({
        title: 'Purchase Failed',
        message: result.error || 'An error occurred during purchase.',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }

    setPurchasing(false);
  };

  // Get monthly and annual packages
  const monthlyPackage = availablePackages.find(
    (p) => p.identifier.toLowerCase().includes('monthly') || p.identifier === '$rc_monthly'
  );
  const annualPackage = availablePackages.find(
    (p) => p.identifier.toLowerCase().includes('annual') || p.identifier === '$rc_annual'
  );

  const selectedPkg = billingCycle === 'annual' ? annualPackage : monthlyPackage;

  // Pricing (fallback if RevenueCat not loaded)
  const monthlyPrice = 19.99;
  const annualPricePerMonth = 15.99;
  const annualTotal = 191.88;
  const savings = ((monthlyPrice * 12 - annualTotal) / (monthlyPrice * 12) * 100).toFixed(0);

  if (isLoading) {
    return (
      <Box pos="relative" mih={200}>
        <LoadingOverlay visible />
      </Box>
    );
  }

  if (!isInitialized) {
    return (
      <Alert color="yellow" title="Subscription Unavailable">
        Unable to load subscription options. Please try again later.
        {contextError && (
          <Text size="sm" mt="xs">
            {contextError}
          </Text>
        )}
      </Alert>
    );
  }

  // Already subscribed
  if (isPremium && !isTrialActive) {
    return (
      <Box>
        <Alert color="green" title="Active Subscription" mb="lg" icon={<IconCheck size={16} />}>
          You have an active Voice Connect subscription. Enjoy all premium features!
        </Alert>
        <Text size="sm" c="dimmed" ta="center">
          Check your subscription receipt emails for instructions on how to cancel, or email support.
        </Text>
      </Box>
    );
  }

  return (
    <Box pos="relative">
      <LoadingOverlay visible={purchasing} />

      {/* Trial status */}
      {isTrialActive && (
        <Alert color="blue" title="Trial Active" mb="lg" icon={<IconInfoCircle size={16} />}>
          You have {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} remaining in your trial. Subscribe
          now to continue access after your trial ends.
        </Alert>
      )}

      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 font-['Poppins']">Choose a Plan</h2>
        <p className="text-gray-600 mt-2">Voice Connect - Complete emotional wellness experience</p>
      </div>

      {/* Two-card grid */}
      <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        {/* Monthly Card */}
        <div
          className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all cursor-pointer ${
            billingCycle === 'monthly' ? 'ring-2 ring-[#C2A4D6]' : 'hover:shadow-xl'
          }`}
          onClick={() => setBillingCycle('monthly')}
        >
          <div className="bg-gray-50 p-6">
            <h3 className="text-xl font-bold text-gray-800 font-['Poppins']">Monthly</h3>
            <div className="flex items-baseline mt-2">
              <span className="text-3xl font-bold text-gray-900">
                {monthlyPackage?.rcBillingProduct?.currentPrice?.formattedPrice || `$${monthlyPrice}`}
              </span>
              <span className="text-gray-600 ml-1">/month</span>
            </div>
          </div>

          <div className="p-6">
            <ul className="space-y-3 mb-6">
              {FEATURES.map((feature) => (
                <li key={feature} className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 text-sm">{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setBillingCycle('monthly');
              }}
              className={`w-full py-3 px-6 rounded-full text-lg font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-[#C2A4D6] text-white'
                  : 'bg-white text-[#C2A4D6] border-2 border-[#C2A4D6] hover:bg-gray-50'
              }`}
            >
              {billingCycle === 'monthly' ? 'Selected' : 'Select'}
            </button>
          </div>
        </div>

        {/* Annual Card */}
        <div
          className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all cursor-pointer relative ${
            billingCycle === 'annual' ? 'ring-2 ring-[#C2A4D6]' : 'hover:shadow-xl'
          }`}
          onClick={() => setBillingCycle('annual')}
        >
          {/* Best Value Badge */}
          <div className="absolute top-0 right-0">
            <div className="bg-[#F4B6A1] text-white px-4 py-1 rounded-bl-lg font-medium text-sm">Best Value</div>
          </div>

          <div className="bg-[#C2A4D6]/10 p-6">
            <h3 className="text-xl font-bold text-gray-800 font-['Poppins']">Annual</h3>
            <div className="flex items-baseline mt-2">
              <span className="text-3xl font-bold text-gray-900">
                {annualPackage?.rcBillingProduct?.currentPrice?.formattedPrice
                  ? `$${annualPricePerMonth}`
                  : `$${annualPricePerMonth}`}
              </span>
              <span className="text-gray-600 ml-1">/month</span>
            </div>
            <p className="text-gray-600 text-sm mt-1">
              Billed ${annualPackage?.rcBillingProduct?.currentPrice?.formattedPrice?.replace('$', '') || annualTotal}
              /year
            </p>
            <p className="text-[#F4B6A1] text-sm font-medium mt-1">Save {savings}%</p>
          </div>

          <div className="p-6">
            <ul className="space-y-3 mb-6">
              {FEATURES.map((feature) => (
                <li key={feature} className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 text-sm">{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setBillingCycle('annual');
              }}
              className={`w-full py-3 px-6 rounded-full text-lg font-medium transition-colors ${
                billingCycle === 'annual'
                  ? 'bg-[#C2A4D6] text-white'
                  : 'bg-white text-[#C2A4D6] border-2 border-[#C2A4D6] hover:bg-gray-50'
              }`}
            >
              {billingCycle === 'annual' ? 'Selected' : 'Select'}
            </button>
          </div>
        </div>
      </div>

      {/* Subscribe Now Button */}
      <div className="max-w-3xl mx-auto mt-8">
        <button
          onClick={() => selectedPkg && handlePurchase(selectedPkg)}
          disabled={purchasing || !selectedPkg}
          className="w-full bg-[#C2A4D6] hover:bg-[#B390C9] disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-4 px-6 rounded-full text-lg font-medium transition-colors"
        >
          {purchasing ? 'Processing...' : 'Subscribe Now'}
        </button>

        <p className="text-center text-gray-500 text-sm mt-4">You can cancel your subscription at any time</p>
      </div>
    </Box>
  );
}

